import { Box, Typography } from "@mui/material";
import { useContext, useState } from "react";
import useAssignUserManagement from "../../../../api/hooks/useAssignUserManagement";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import AssigneeSelector from "../../../../components/assignee/AssigneeSelector";
import StatusChip from "../../../../components/chips/StatusChip";
import CustomToolTip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import ViewedTodayTooltip from "../../../../components/poc/ViewedTodayTooltip";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import { POCContext } from "../../../../contexts/POCContext";
import SvgToIcon from "../../../../icons/SvgToIcon";
import {
  getRemediationPropLabel,
  RemediationTypeChip,
} from "../../../../utils/chipsData";
import {
  PLATFORM_USER_ACTIONS,
  RemediationType,
} from "../../../../utils/constants";
import ImpactChip from "../../../posture/controls/components/ControlRemediationDrawer/control/ImpactChip";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import AutoRemediationAvailableChip from "./AutoRemediationAvailableChip";
import ChipColor from "./ChipColor";
import CustomPrioritySelect from "./CustomPrioritySelect";

export default function RemediationTable({
  onRowClick,
  remediations,
  loading,
  refToPass = undefined,
  onRowSelection,
  selectedRowIDs = undefined,
  viewedControls = [],
  controls = [],
}) {
  const { isPOC } = useContext(POCContext);
  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.ASSIGN_REMEDIATION_TO_USER
  );
  const { autoRemediationActive } = useContext(CustomerFeatureFlagsContext);
  const colors = useColors();
  const {
    usersMap,
    usersLoading,
    handleAssignControlAndRemediation,
    isControlRemediationLoading,
  } = useAssignUserManagement();

  const [sortModel, setSortModel] = useState([
    {
      field: "status",
      sort: "asc",
    },
    {
      field: "custom_priority",
      sort: "desc",
    },
    {
      field: "feasibility",
      sort: "desc",
    },
    {
      field: "impact",
      sort: "desc",
    },
  ]);

  const getControlByRemediationId = (remediationId) => {
    return (
      controls?.find((control) => control.control_id === remediationId) || null
    );
  };

  const remediationPOCColumn = {
    field: "remediation_id",
    headerName: "",
    flex: 1,
    renderCell: (params) => {
      let isViewed = false;
      if (Array.isArray(viewedControls)) {
        // Check if the control is viewed today
        isViewed = viewedControls.includes(params.value.toString());
      }
      return (
        <Box display="flex" alignItems="center" sx={{ marginLeft: "6px" }}>
          {isViewed && <ViewedTodayTooltip />}
        </Box>
      );
    },
  };

  const columns = [
    ...(isPOC ? [remediationPOCColumn] : []),
    {
      field: "id",
      headerName: "ID",
      width: 70,
      renderCell: (params) => {
        return <Typography variant="body2">#{params.value}</Typography>;
      },
    },
    {
      field: "name",
      flex: 0.45,
      minWidth: 380,
      headerName: "Remediation Name",
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" gap={1}>
            {autoRemediationActive && params.row.has_autofix && (
              <AutoRemediationAvailableChip handleClick={() => {}} />
            )}
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: "tool",
      headerName: "Tool",
      width: 60,
      valueFormatter: (params) => {
        const displayName =
          IntegrationsMapping[params.value?.toLowerCase()]?.displayName;
        return displayName || String(params.value || "");
      },
      renderCell: (params) => (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width={"100%"}
        >
          <CustomToolTip
            title={IntegrationsMapping[params.value.toLowerCase()].displayName}
          >
            <Box>
              <SvgToIcon
                IconLogo={IntegrationsMapping[params.value.toLowerCase()].logo}
                width={40}
              />
            </Box>
          </CustomToolTip>
        </Box>
      ),
    },
    {
      field: "custom_priority",
      headerName: "Priority",
      width: 150,
      valueFormatter: (params) => {
        return getRemediationPropLabel(
          RemediationType.REMEDIATION_PRIORITY,
          params.value
        );
      },
      valueGetter: (params) => {
        const priority =
          params.row.custom_priority > 0
            ? params.row.custom_priority
            : params.row.priority;
        return priority;
      },
      renderCell: (params) => {
        return (
          <CustomPrioritySelect
            row={params.row}
            renderValue={(customPriority) => (
              <ChipColor
                value={customPriority}
                type={RemediationType.REMEDIATION_PRIORITY}
                sx={{ cursor: "pointer" }}
                width={100}
              />
            )}
          />
        );
      },
    },
    {
      field: "feasibility",
      headerName: "Feasibility",
      width: 130,
      valueFormatter: (params) => {
        return getRemediationPropLabel(
          RemediationType.FEASIBILITY,
          params.value
        );
      },
      renderCell: (params) => {
        return (
          <ChipColor value={params.value} type={RemediationType.FEASIBILITY} />
        );
      },
    },
    {
      field: "impact",
      headerName: "Resilience Impact",
      width: 180,
      valueFormatter: (params) => {
        return getRemediationPropLabel(RemediationType.IMPACT, params.value);
      },
      renderCell: (params) => {
        return <ChipColor value={params.value} type={RemediationType.IMPACT} />;
      },
    },

    {
      field: "status",
      headerName: "Status",
      width: 150,
      valueFormatter: (params) => {
        return params.value;
      },
      renderCell: (params) => {
        return (
          <StatusChip
            circleColor={
              colors[
                RemediationTypeChip["status"][params.value].color + "_text"
              ]
            }
            label={params.value}
          />
        );
      },
    },
    {
      field: "assignee_email",
      headerName: "Assignee",
      width: 200,
      valueFormatter: (params) => {
        const assignee = usersMap.get(params.value);
        return assignee?.display_name || params.value || "Unassigned";
      },
      renderCell: (params) => {
        const selectedAssignee = usersMap.get(params.value) || null;
        const remediationId = params.row.remediation_id;

        const handleAssigneeChange = (assignee) => {
          handleAssignControlAndRemediation(remediationId, assignee);
        };

        if (usersLoading) {
          return <LoadingIcon size={12} />;
        }

        return (
          <AssigneeSelector
            value={selectedAssignee ? selectedAssignee : null}
            onChange={handleAssigneeChange}
            size="small"
            loading={isControlRemediationLoading(params.row.remediation_id)}
            hasPermission={hasPermission}
          />
        );
      },
    },

    {
      field: "affected_entities_count",
      headerName: "Affected Entities",
      width: 150,
      valueGetter: (params) => {
        const control = getControlByRemediationId(params.row.control_id);
        if (!control) return 0;
        return control?.impacted_included_entities_count || 0;
      },
      renderCell: (params) => {
        const control = getControlByRemediationId(params.row.control_id);
        if (!control) {
          return (
            <Typography variant="body2" color={colors.text_secondary}>
              -
            </Typography>
          );
        }
        return (
          <ImpactChip
            numberOfEntities={control?.impacted_included_entities_count || 0}
          />
        );
      },
    },
  ];

  return (
    <StyledDataGrid
      sx={{
        cursor: "pointer",
        overflowX: "auto",
        "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
          {
            paddingLeft: "16px",
          },
        "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
          {
            paddingRight: "16px",
          },
        "& .MuiDataGrid-virtualScroller": {
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#555",
          },
        },
      }}
      boxSXProps={{
        overflow: "auto", // Override the default "hidden" overflow from StyledDataGrid
        "& .MuiDataGrid-root": {
          border: "none",
          boxShadow: "none",
        },
      }}
      columns={columns}
      pageSizeOptionsOverride={[8, 20, 50]}
      rows={remediations}
      onRowClick={onRowClick}
      onRowSelectionModelChange={onRowSelection}
      rowSelectionModel={selectedRowIDs}
      loading={loading}
      apiRef={refToPass}
      sortModel={sortModel}
      onSortModelChange={(model) => setSortModel(model)}
      checkboxSelection
      disableRowSelectionOnClick
    />
  );
}
